import React, { useEffect, useState, useCallback, useRef } from 'react';
import './styles.css';
import './styles/toggle-buttons.css';
import './styles/split-view.css';
import AppHeader from './components/AppHeader';
import OpenHouseGalleryView from './views/OpenHouseGalleryView';
import OpenHouseMapView from './views/OpenHouseMapView';
import ActiveListingsGalleryView from './views/ActiveListingsGalleryView';
import ActiveListingsMapView from './views/ActiveListingsMapView';
import agent from './agent.js';
import BottomNavigation from './components/BottomNavigation';
import Footer from './components/Footer/Footer';
import { StyleConstants } from './StyleConstants';
import analyticsService from './services/analyticsService';
import mapboxgl from 'mapbox-gl';

const TABLET_MAX_WIDTH = 1190; // Devices up to this width will get the mobile header

function App() {

  const getShouldRenderMobileLayout = useCallback(() => {
    return window.innerWidth <= TABLET_MAX_WIDTH;
  }, []);

  const [shouldRenderMobileLayout, setShouldRenderMobileLayout] = useState(getShouldRenderMobileLayout());
  const [viewMode, setViewMode] = useState('gallery');
  const [dataMode, setDataMode] = useState('openhouses');
  const [filterStatus, setFilterStatus] = useState('all');
  const [galleryResetKey, setGalleryResetKey] = useState(0);
  
  // Ref for the gallery scroll container
  const galleryScrollRef = useRef(null);
  const previousDataModeRef = useRef(dataMode);
  
  const initialFilterStatus = 'all';

  // Open house data is now managed by the individual view components via useOpenHouses hook

  // Update layout state on resize or orientation change
  useEffect(() => {
    const handleLayoutChange = () => {
      const newLayoutIsMobile = getShouldRenderMobileLayout();
      setShouldRenderMobileLayout(newLayoutIsMobile);
      // Split view is no longer available, so no need to check for it
    };
    
    window.addEventListener('resize', handleLayoutChange);
    window.addEventListener('orientationchange', handleLayoutChange);
    handleLayoutChange(); // Initial check
    
    return () => {
      window.removeEventListener('resize', handleLayoutChange);
      window.removeEventListener('orientationchange', handleLayoutChange);
    };
  }, [viewMode, getShouldRenderMobileLayout]);

  const handleGoHome = useCallback(() => {
    const homeViewMode = 'gallery'; // Always go to gallery view when clicking home
    
    // Track home navigation
    analyticsService.trackEvent({
      category: 'Navigation',
      action: 'Go Home',
      label: viewMode === 'gallery' ? 'Scroll to Top' : 'Return to Gallery',
      value: 1
    });
    
    // If already in gallery view, scroll to top instead of resetting
    if (viewMode === 'gallery') {
      if (galleryScrollRef.current) {
        galleryScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Fallback to window scroll if ref isn't available
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      // Force the gallery view to remount so its day filter resets to "All Days"
      setGalleryResetKey(prev => prev + 1);
      return;
    }
    
    setViewMode(homeViewMode);
    // Keep current dataMode (Open Houses vs All Listings) on "home"
    setFilterStatus(initialFilterStatus);
  }, [viewMode]);

  // Effect to set .app height dynamically
  useEffect(() => {
    const appElement = document.querySelector('.app');
    const setAppHeight = () => {
      if (appElement) {
        if (shouldRenderMobileLayout) {
          appElement.style.height = `${window.innerHeight}px`;
        } else {
          appElement.style.height = '100%'; 
        }
      }
    };
    setAppHeight();
    window.addEventListener('resize', setAppHeight);
    window.addEventListener('orientationchange', setAppHeight);

    return () => {
      window.removeEventListener('resize', setAppHeight);
      window.removeEventListener('orientationchange', setAppHeight);
    };
  }, [shouldRenderMobileLayout]);

  const handleViewModeChange = (mode) => {
    // Split view is no longer available
    setViewMode(mode);
    
    // Track view mode changes
    analyticsService.trackEvent({
      category: 'Navigation',
      action: 'View Mode Changed',
      label: mode,
      value: 1
    });
  };

  // Warm up Mapbox GL so the first map mount is faster, without keeping a live map under the gallery.
  useEffect(() => {
    let idleId = null;
    let timeoutId = null;
    const prewarm = () => {
      try {
        if (typeof mapboxgl?.prewarm === 'function') {
          mapboxgl.prewarm();
        }
      } catch {
        // Ignore prewarm failures; app still works.
      }
    };

    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(prewarm, { timeout: 1500 });
    } else {
      timeoutId = window.setTimeout(prewarm, 750);
    }

    return () => {
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  /**
   * When switching between Open Houses and All Listings, reset the shared gallery
   * scroll container to the top. Without this, the new dataset renders at the
   * previous scroll position, which feels broken.
   */
  useEffect(() => {
    const previousDataMode = previousDataModeRef.current;
    if (previousDataMode !== dataMode && viewMode === 'gallery') {
      // Ensure we reset the actual scroll container (not the window).
      if (galleryScrollRef.current) {
        galleryScrollRef.current.scrollTo({ top: 0, behavior: 'auto' });
      } else {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    }
    previousDataModeRef.current = dataMode;
  }, [dataMode, viewMode]);

  const handleBottomNavNavigate = ({ data, view }) => {
    // Timing for view navigation
    console.time(`Navigation-To-${data}-${view}`);
    setDataMode(data);
    setViewMode(view);
    
    // Track mobile navigation
    analyticsService.trackEvent({
      category: 'Mobile Navigation',
      action: 'Bottom Nav Click',
      label: `${data}-${view}`,
      value: 1
    });
    
    setTimeout(() => {
      console.timeEnd(`Navigation-To-${data}-${view}`);
      console.time('OpenHouses-View-Render');
    }, 0);
  };

  const renderContent = () => {
    const mapViewWrapperStyle = {
      flex: '1 1 auto',
      width: '100%',
      overflow: 'hidden',
      position: 'relative',
      boxSizing: 'border-box'
    };

    const renderGallery = (content) => (
      <>
        <div
          ref={galleryScrollRef}
          style={{
            flexGrow: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            backgroundColor: 'var(--grey)',
            padding: `${StyleConstants.contentTopPadding} ${StyleConstants.contentSidePadding} 20px ${StyleConstants.contentSidePadding}`,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {content}
        </div>
        {!shouldRenderMobileLayout && <Footer />}
      </>
    );

    if (dataMode === 'listings') {
      if (viewMode === 'map') {
        return (
          <div style={mapViewWrapperStyle}>
            <ActiveListingsMapView isMobile={shouldRenderMobileLayout} />
          </div>
        );
      }
      return renderGallery(<ActiveListingsGalleryView key={galleryResetKey} isMobile={shouldRenderMobileLayout} />);
    }

    // default: open houses
    if (viewMode === 'map') {
      return (
        <div style={mapViewWrapperStyle}>
          <OpenHouseMapView isMobile={shouldRenderMobileLayout} />
        </div>
      );
    }
    return renderGallery(<OpenHouseGalleryView key={galleryResetKey} isMobile={shouldRenderMobileLayout} />);
  };



  return (
    <div className="app" style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%', 
      width: '100%',
      overflow: 'hidden'
    }}>
      <div
        id="app-header-wrapper"
        style={{
          flexShrink: 0,
          width: '100%',
          // Keep header controls (hamburger, toggles) above the map when the map is mounted/visible.
          position: 'relative',
          zIndex: 3000
        }}
      >
        <AppHeader
          agent={agent}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          dataMode={dataMode}
          onDataModeChange={setDataMode}
          isMobile={shouldRenderMobileLayout} // This prop name is kept for AppHeader's internal use
          onGoHome={handleGoHome}
        />
      </div>
      <main id="main-content-area" style={{ 
        flexGrow: 1, 
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {renderContent()}
      </main>
      {shouldRenderMobileLayout && (
        <div style={{ flexShrink: 0, width: '100%' }} id="mobile-bottom-nav-wrapper">
          <BottomNavigation
            activeView={{ dataMode, viewMode }}
            onNavigate={handleBottomNavNavigate}
            className="mobile-visible"
          />
        </div>
      )}
    </div>
  );
}

export default App;
